import { useEffect, useMemo, useState } from "react"
import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { useTheme } from "@mui/material"
import fp from "lodash/fp"

import { useLazySpanQuery, useLazyConductorQuery } from "features/api"
import type {
  DevicePlotDef,
  PlotView,
  LinePlotView,
  SingleStatPlotView,
} from "types/dashboard.types"
import type { Device } from "types/device.types"
import { selectCurrentOrgId } from "features/store/orgSlice"
import type { LabelAlign } from "types/viewProperties.types"
import { axisToFormatterProps, getFormatter } from "widgets/plots/configuration/helpers"

export function useBendingAmplitudeViews(def: DevicePlotDef, device?: Device) {
  const { t, i18n } = useTranslation()
  const locale = i18n.resolvedLanguage
  const theme = useTheme()
  const orgId = useSelector(selectCurrentOrgId)
  const [getSpan] = useLazySpanQuery()
  const [getConductor] = useLazyConductorQuery()

  const [viewWExtras, setViewWExtras] = useState<PlotView>(def.view)

  const isBendingAmplitudeLinePlot = useMemo(
    () => def.view.nameKey === "BENDING_AMPLITUDE" && def.view.type === "xy",
    [def],
  )

  const isDataAboveBendingAmplitudeLimitPlot = useMemo(
    () => def.view.nameKey === "PERCENTAGE_ABOVE_BENDING_AMPLITUDE_LIMIT",
    [def],
  )

  const spanId = device?.settings?.span_id

  useEffect(() => {
    if (
      (!isBendingAmplitudeLinePlot && !isDataAboveBendingAmplitudeLimitPlot) ||
      !spanId
    ) {
      setViewWExtras(def.view)
      return
    }

    const fetchData = async () => {
      try {
        const span = await getSpan(
          {
            id: spanId,
            params: { org_id: Number(orgId) },
          },
          true,
        ).unwrap()

        const conductor = await getConductor({ id: span.conductor_id }, true).unwrap()

        if (isBendingAmplitudeLinePlot) {
          const viewAsXY = def.view as LinePlotView
          const formatter = getFormatter(
            axisToFormatterProps(viewAsXY.properties.axes.y, viewAsXY.properties, locale),
          )

          const formattedLabel = `${t("plots_references.SAFE_LIMIT")}: ${formatter(
            conductor.bending_amplitude_limit,
          )}`

          const newView = fp.merge(viewAsXY, {
            properties: {
              ...(conductor.bending_amplitude_limit != null && {
                axes: {
                  y: {
                    bounds: ["0", `${conductor.bending_amplitude_limit}`],
                  },
                },
                yReferenceLines: [
                  ...(viewAsXY.properties?.yReferenceLines ?? []),
                  {
                    y: conductor.bending_amplitude_limit,
                    label: formattedLabel,
                    labelAlign: "start" as LabelAlign,
                    labelStyle: {
                      fill: theme.palette.error.main,
                      fontSize: "0.7rem",
                      paintOrder: "stroke",
                      stroke: theme.palette.background.paper,
                      strokeWidth: 3,
                    },
                    lineStyle: {
                      strokeDasharray: "4 4",
                      strokeWidth: 0.75,
                      stroke: theme.palette.error.main,
                      shapeRendering: "geometricPrecision",
                    },
                  },
                ],
              }),
            },
          })
          setViewWExtras(newView)
        } else if (isDataAboveBendingAmplitudeLimitPlot) {
          const viewAsSingleStat = def.view as SingleStatPlotView
          const newView = fp.merge(viewAsSingleStat, {
            properties: {
              dataThreshold: conductor.bending_amplitude_limit,
            },
          })
          setViewWExtras(newView)
        }
      } catch {
        setViewWExtras(def.view)
      }
    }

    fetchData()
  }, [
    isBendingAmplitudeLinePlot,
    isDataAboveBendingAmplitudeLimitPlot,
    spanId,
    orgId,
    t,
    locale,
    def.view,
    getSpan,
    getConductor,
    theme.palette,
  ])

  return {
    view: viewWExtras,
  }
}
